import type { GlobeCityNode } from '../components/VisitorGlobe';

export interface CurrentVisitorInfo {
  ip_address: string;
  city: string;
  region: string;
  country: string;
  country_code: string;
  latitude: number;
  longitude: number;
  timezone: string;
  isp: string;
  browser: string;
  os: string;
  device_type: string;
  screen_resolution: string;
  language: string;
  your_visit_count: number;
  your_total_pageviews: number;
  first_seen_at: string;
  last_seen_at: string;
}

export interface OverviewMetrics {
  total_visits: number;
  unique_visitors: number;
  total_sessions: number;
  returning_visitors: number;
  returning_rate_pct: number;
  avg_pageviews_per_visitor: number;
  active_visitors_24h: number;
  visits_last_24h: number;
  distinct_countries: number;
  distinct_cities: number;
  avg_load_time_ms: number;
  peak_hour_utc: string;
  furthest_city: string;
  furthest_distance_km: number;
  northern_hemisphere_pct: number;
  eastern_hemisphere_pct: number;
}

export interface ContentSystemMetrics {
  writings_count: number;
  writings_total_words: number;
  projects_count: number;
  books_count: number;
  books_completed_count: number;
  books_reading_count: number;
  db_size_bytes: number;
  total_db_rows: number;
  server_uptime_seconds: number;
}

export interface BreakdownItem {
  label: string;
  count: number;
  percentage: number;
}

export interface StatsBreakdowns {
  browsers: BreakdownItem[];
  operating_systems: BreakdownItem[];
  devices: BreakdownItem[];
  top_pages: BreakdownItem[];
  top_countries: BreakdownItem[];
  screen_resolutions: BreakdownItem[];
  referrers: BreakdownItem[];
}

export interface RecentVisitItem {
  id: number;
  masked_ip: string;
  city: string;
  country: string;
  country_code: string;
  path: string;
  browser: string;
  os: string;
  device_type: string;
  load_time_ms: number;
  created_at: string;
}

export interface StatsResponse {
  current_visitor: CurrentVisitorInfo;
  overview: OverviewMetrics;
  content_stats: ContentSystemMetrics;
  cities: GlobeCityNode[];
  breakdowns: StatsBreakdowns;
  recent_visits: RecentVisitItem[];
}

const SESSION_STORAGE_KEY = 'arstep_telemetry_session_id';
const STATS_CACHE_STORAGE_KEY = 'arstep_telemetry_stats_cache_v1';

function loadPersistedStatsCache(): StatsResponse | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(STATS_CACHE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StatsResponse;
    if (parsed && parsed.current_visitor && parsed.overview && Array.isArray(parsed.cities)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function savePersistedStatsCache(data: StatsResponse): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STATS_CACHE_STORAGE_KEY, JSON.stringify(data));
    }
  } catch {
    // Ignore storage quota or private browsing errors
  }
}

let latestStatsCache: StatsResponse | null = loadPersistedStatsCache();
let inFlightPromise: Promise<StatsResponse | null> | null = null;
let lastRecordedPath = '';
let lastRecordedAt = 0;
const listeners = new Set<(stats: StatsResponse) => void>();

function getOrCreateSession(): { sessionId: string; isNewSession: boolean } {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      return { sessionId: existing, isNewSession: false };
    }
    const generated =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
    return { sessionId: generated, isNewSession: true };
  } catch {
    return { sessionId: 'sess_ephemeral', isNewSession: false };
  }
}

export function getClientTelemetryEnvironment() {
  const tz =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
      : 'UTC';
  const lang =
    typeof navigator !== 'undefined' ? navigator.language || 'en-US' : 'en-US';
  const screenRes =
    typeof window !== 'undefined' && window.screen
      ? `${window.screen.width}x${window.screen.height}`
      : '1920x1080';
  const viewport =
    typeof window !== 'undefined'
      ? `${window.innerWidth}x${window.innerHeight}`
      : '1440x900';
  const colorDepth =
    typeof window !== 'undefined' && window.screen
      ? window.screen.colorDepth || 24
      : 24;

  const navAny = typeof navigator !== 'undefined' ? (navigator as unknown as Record<string, unknown>) : {};
  const deviceMemory =
    typeof navAny.deviceMemory === 'number' ? (navAny.deviceMemory as number) : undefined;
  const hardwareConcurrency =
    typeof navigator !== 'undefined' && typeof navigator.hardwareConcurrency === 'number'
      ? navigator.hardwareConcurrency
      : undefined;

  const conn = navAny.connection as Record<string, unknown> | undefined;
  const connectionType =
    conn && typeof conn.effectiveType === 'string' ? (conn.effectiveType as string) : undefined;

  let loadTimeMs = 42;
  if (typeof performance !== 'undefined') {
    const navEntries = performance.getEntriesByType?.('navigation') as PerformanceNavigationTiming[] | undefined;
    if (navEntries && navEntries.length > 0) {
      const entry = navEntries[0];
      const dur = Math.round(entry.domContentLoadedEventEnd || entry.responseEnd || entry.duration || 42);
      if (dur > 0 && dur < 60000) {
        loadTimeMs = dur;
      }
    }
  }

  return {
    timezone: tz,
    language: lang,
    screen_resolution: screenRes,
    viewport,
    color_depth: colorDepth,
    device_memory_gb: deviceMemory,
    hardware_concurrency: hardwareConcurrency,
    connection_type: connectionType,
    load_time_ms: loadTimeMs,
  };
}

export function getCachedStats(): StatsResponse | null {
  if (!latestStatsCache) {
    latestStatsCache = loadPersistedStatsCache();
  }
  return latestStatsCache;
}

export function subscribeToStats(cb: (stats: StatsResponse) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function notifyListeners(data: StatsResponse) {
  latestStatsCache = data;
  savePersistedStatsCache(data);
  listeners.forEach((cb) => cb(data));
}

export async function recordPageVisit(pathname: string): Promise<StatsResponse | null> {
  const now = Date.now();
  if (pathname === lastRecordedPath && now - lastRecordedAt < 2000) {
    if (inFlightPromise) {
      return inFlightPromise;
    }
    if (latestStatsCache) {
      return latestStatsCache;
    }
  }
  lastRecordedPath = pathname;
  lastRecordedAt = now;

  const { sessionId, isNewSession } = getOrCreateSession();
  const env = getClientTelemetryEnvironment();

  const payload = {
    path: pathname || '/',
    referrer: typeof document !== 'undefined' ? document.referrer || 'Direct' : 'Direct',
    session_id: sessionId,
    is_new_session: isNewSession,
    ...env,
  };

  const promise = (async (): Promise<StatsResponse | null> => {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await fetch('/api/stats/visit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const data = (await res.json()) as StatsResponse;
          notifyListeners(data);
          return data;
        }
      } catch {
        // Network error or interrupted request; retry below
      }
      if (attempt === 0) {
        await new Promise((r) => setTimeout(r, 300));
      }
    }
    return null;
  })().finally(() => {
    if (inFlightPromise === promise) {
      inFlightPromise = null;
    }
  });

  inFlightPromise = promise;
  return promise;
}

export async function fetchStatsSnapshot(): Promise<StatsResponse | null> {
  if (inFlightPromise) {
    try {
      const res = await inFlightPromise;
      if (res) return res;
    } catch {
      // Fall through to explicit GET /api/stats
    }
  }
  const env = getClientTelemetryEnvironment();
  const params = new URLSearchParams({
    tz: env.timezone,
    lang: env.language,
    res: env.screen_resolution,
  });

  const delays = [300, 700, 1200];
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try {
      const res = await fetch(`/api/stats?${params.toString()}`, {
        cache: 'no-store',
      });
      if (res.ok) {
        const data = (await res.json()) as StatsResponse;
        notifyListeners(data);
        return data;
      }
    } catch {
      // Retry after short delay
    }
    if (attempt < delays.length) {
      await new Promise((r) => setTimeout(r, delays[attempt]));
    }
  }
  return latestStatsCache;
}
